/**
 * Hero copy stack — positioning line, product support line, and highlight chips (PBI #1238).
 *
 * Business rules:
 * - Keep the first viewport compact and scannable (benefit → products → chips → CTA).
 * - "One-stop shop" is the sole inline accent so the middle line stays calm.
 * - Soft wash adds depth without a left accent bar or multi-color product nouns.
 * - Chip icons: Auto Send = refresh; AI = sparkles (product AI cue); control = adjustments
 *   (you stay in the driver's seat — review / tweak before anything goes out).
 * - Motion is decorative only; respects prefers-reduced-motion.
 */

import { Badge, Group, Stack, Text, rem } from '@mantine/core';
import {
  IconAdjustments,
  IconRefresh,
  IconSparkles,
} from '@tabler/icons-react';
import type { ComponentType, ReactNode } from 'react';
import { brandColors, brandFonts, semanticColors } from '../../config/theme';
import { landingContent } from '../../content/landingContent';
import type { HeroHighlightIconKey } from './HeroHighlightIconKey';
import styles from './HeroCopyStack.module.css';

const HIGHLIGHT_ICONS: Record<
  HeroHighlightIconKey,
  ComponentType<{ size?: number | string; stroke?: number }>
> = {
  autoSend: IconRefresh,
  ai: IconSparkles,
  control: IconAdjustments,
};

const HIGHLIGHT_COLORS: Record<HeroHighlightIconKey, string> = {
  autoSend: brandColors.scheduling,
  ai: brandColors.feature,
  control: brandColors.primary,
};

/** Phrase emphasized inside the positioning line (must appear in heroBenefit). */
const ONE_STOP_ACCENT = 'one-stop shop';

/**
 * Wraps the one-stop-shop phrase in an accent span for visual hierarchy.
 */
function renderBenefitWithAccent(benefit: string): ReactNode {
  const index = benefit.toLowerCase().indexOf(ONE_STOP_ACCENT);
  if (index < 0) {
    return benefit;
  }

  const before = benefit.slice(0, index);
  const accent = benefit.slice(index, index + ONE_STOP_ACCENT.length);
  const after = benefit.slice(index + ONE_STOP_ACCENT.length);
  return (
    <>
      {before}
      <span className={styles.accent}>{accent}</span>
      {after}
    </>
  );
}

/**
 * Renders the styled hero copy column: quiet wash, accented positioning line,
 * calm product support line, and light highlight chips.
 */
export const HeroCopyStack = () => {
  const { heroBenefit, heroCategory, heroHighlights } = landingContent.brand;

  return (
    <Stack
      gap="sm"
      maw={rem(520)}
      className={styles.root}
      data-testid="hero-copy-stack"
    >
      <Text
        size="xl"
        fw={700}
        c={semanticColors.text.heading}
        style={{ lineHeight: 1.3, fontFamily: brandFonts.headline }}
      >
        {renderBenefitWithAccent(heroBenefit)}
      </Text>

      <Text size="md" c={semanticColors.text.body} style={{ lineHeight: 1.55 }}>
        {heroCategory}
      </Text>

      <Group gap="xs" wrap="wrap">
        {heroHighlights.map((item) => {
          const Icon = HIGHLIGHT_ICONS[item.icon];
          return (
            <Badge
              key={item.label}
              className={styles.chip}
              variant="light"
              color={HIGHLIGHT_COLORS[item.icon]}
              size="lg"
              radius="xl"
              leftSection={<Icon size={14} stroke={2} />}
              styles={{
                root: {
                  textTransform: 'none',
                  fontWeight: 600,
                  paddingInline: rem(12),
                  height: rem(32),
                },
                section: { marginInlineEnd: rem(6) },
              }}
            >
              {item.label}
            </Badge>
          );
        })}
      </Group>
    </Stack>
  );
};
